<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'CreateStampCampaign' }" class="btn btn-primary">
                <i class="ri-add-line"></i>
                新增集章活動
              </router-link>
      </div>
    </div>

    <div class="page-container">
    <div v-if="isWixtarClient" class="wixtar-stamp-notice">
      <i class="ri-information-line"></i>
      <div>
        <p class="wixtar-stamp-notice-title">Wixtar 交易觸發集章</p>
        <p>
          預設不會因 POS／Lockline 商城交易自動發章（商城單也會先到 Wixtar，再經 webhook）。
          可至
          <router-link to="/client/settings?tab=wixtar">系統參數 → Wixtar 整合</router-link>
          開啟，或直接使用下方開關。
        </p>
        <p v-if="!canEnableWixtarStampIssue" class="notice-off">
          目前 Wixtar 整合未啟用或「同步訂單」已關閉，無法開啟交易觸發集章。請先到
          <router-link to="/client/settings?tab=wixtar">Wixtar 整合</router-link>
          完成設定。
        </p>
        <div class="wixtar-stamp-switch-row">
          <span>接受 Wixtar webhook 交易事件觸發集章</span>
          <label class="toggle-switch" :class="{ disabled: !canEnableWixtarStampIssue }">
            <input
              v-model="wixtarTransactionStampIssueEnabled"
              type="checkbox"
              :disabled="wixtarStampSettingSaving || !canEnableWixtarStampIssue"
              @change="saveWixtarStampIssueSetting"
            />
            <span class="toggle-slider"></span>
          </label>
          <span class="toggle-status" :class="wixtarTransactionStampIssueEnabled ? 'on' : 'off'">
            {{ wixtarStampSettingSaving ? '儲存中...' : (wixtarTransactionStampIssueEnabled ? '已啟用' : '已停用') }}
          </span>
        </div>
        <p v-if="wixtarTransactionStampIssueEnabled" class="notice-on">
          已開啟：交易 webhook 同步為已完成且已付款時，會依各活動交易規則自動發章。未開亦可使用 QR／人工補章。
        </p>
        <p v-else-if="canEnableWixtarStampIssue" class="notice-off">
          目前關閉：請改用 QR 補章或人工補章。
        </p>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱或代碼..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchCampaigns">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>活動代碼</th>
            <th>活動名稱</th>
            <th>規則類型</th>
            <th>總章數</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="campaigns.length === 0">
            <td colspan="7" class="empty-state">沒有找到集章活動</td>
          </tr>
          <tr v-for="campaign in campaigns" :key="campaign.id">
            <td>{{ campaign.campaignCode }}</td>
            <td>{{ campaign.campaignName }}</td>
            <td>{{ getRuleTypeText(campaign.ruleType) }}</td>
            <td>{{ campaign.totalStamps }}</td>
            <td>
              <span :class="getStatusClass(campaign.isActive)">
                {{ getStatusText(campaign.isActive) }}
              </span>
            </td>
            <td>{{ formatDate(campaign.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <router-link
                  :to="{ name: 'EditStampCampaign', params: { id: campaign.id } }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </router-link>
                <button class="btn btn-sm btn-danger" @click="handleDelete(campaign)">
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { stampCampaignService } from '@/api/modules/stampCampaigns.js';
import { clientService } from '@/api/modules/clients.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import authService from '@/helpers/authservice/authService.js';
import { showDeleteConfirm, showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'StampCampaignList',
  setup() {
    const { pageTitle } = usePageTitle('集章活動管理');
    const campaigns = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const isWixtarClient = ref(false);
    const wixtarConfig = ref(null);
    const wixtarTransactionStampIssueEnabled = ref(false);
    const wixtarStampSettingSaving = ref(false);
    const canEnableWixtarStampIssue = computed(() => {
      const cfg = wixtarConfig.value;
      if (!cfg?.isActive) return false;
      if (cfg.syncOrders === false) return false;
      return true;
    });
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });

    const fetchCampaigns = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };
        if (searchQuery.value) {
          params.search = searchQuery.value;
        }
        if (filterStatus.value === 'active') {
          params.isActive = true;
        } else if (filterStatus.value === 'inactive') {
          params.isActive = false;
        }

        const response = await stampCampaignService.getCampaigns(params);
        if (response?.success && response.data) {
          campaigns.value = response.data.items || [];
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
        } else {
          error.value = response?.message || '獲取集章活動列表失敗';
        }
      } catch (err) {
        console.error('獲取集章活動列表失敗:', err);
        error.value = err.message || '獲取集章活動列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchCampaigns();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchCampaigns();
      }
    };

    const handleDelete = async (campaign) => {
      const confirmed = await showDeleteConfirm(campaign.campaignName, [
        '集章規則',
        '獎勵設定',
        '會員集章進度',
        '發章記錄'
      ]);
      if (!confirmed) return;

      try {
        const response = await stampCampaignService.deleteCampaign(campaign.id);
        if (response?.success) {
          await showSuccess('刪除成功', '集章活動已刪除');
          await fetchCampaigns();
        } else {
          await showError('刪除失敗', response?.message || '刪除集章活動失敗');
        }
      } catch (err) {
        console.error('刪除集章活動失敗:', err);
        await showError('刪除失敗', err.message || '刪除集章活動失敗');
      }
    };

    const getRuleTypeText = (ruleType) => {
      const ruleMap = {
        order_once: '每筆交易一次',
        order_amount: '單筆交易滿額'
      };
      return ruleMap[ruleType] || ruleType || '-';
    };

    const getStatusText = (isActive) => {
      return isActive ? '啟用' : '停用';
    };

    const getStatusClass = (isActive) => {
      if (isActive) return 'badge badge-success';
      return 'badge badge-secondary';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW');
    };

    const loadWixtarStampIssueSetting = async () => {
      try {
        const [wixtarRes, tierRes] = await Promise.all([
          clientSettingsService.get(NAMESPACES.WIXTAR),
          clientSettingsService.get(NAMESPACES.MEMBER_TIER)
        ]);
        const wixtar = wixtarRes?.data?.settings ?? {};
        const tier = tierRes?.data?.settings ?? {};
        wixtarTransactionStampIssueEnabled.value = !!(
          wixtar.wixtarTransactionStampIssueEnabled || tier.wixtarTransactionStampIssueEnabled
        );
      } catch (err) {
        console.error('載入 Wixtar 交易集章設定失敗:', err);
        wixtarTransactionStampIssueEnabled.value = false;
      }
    };

    const saveWixtarStampIssueSetting = async () => {
      wixtarStampSettingSaving.value = true;
      try {
        if (wixtarTransactionStampIssueEnabled.value && !canEnableWixtarStampIssue.value) {
          wixtarTransactionStampIssueEnabled.value = false;
          await showError('無法開啟', '請先啟用 Wixtar 整合並開啟「同步訂單」');
          return;
        }
        await clientSettingsService.merge(NAMESPACES.WIXTAR, {
          wixtarTransactionStampIssueEnabled: !!wixtarTransactionStampIssueEnabled.value
        });
        await clientSettingsService.merge(NAMESPACES.MEMBER_TIER, {
          wixtarTransactionStampIssueEnabled: false
        });
        await showSuccess('已儲存', wixtarTransactionStampIssueEnabled.value
          ? '已開啟 Wixtar 交易觸發集章'
          : '已關閉 Wixtar 交易觸發集章');
      } catch (err) {
        console.error('儲存 Wixtar 交易集章設定失敗:', err);
        wixtarTransactionStampIssueEnabled.value = !wixtarTransactionStampIssueEnabled.value;
        await showError('儲存失敗', err.message || '無法更新設定');
      } finally {
        wixtarStampSettingSaving.value = false;
      }
    };

    const loadWixtarContext = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user?.clientId) {
          isWixtarClient.value = false;
          return;
        }
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.success && res.data?.clientData?.memberSystemType === 'WIXTAR';
        if (!isWixtarClient.value) return;
        try {
          const cfgRes = await wixtarService.getConfig(false);
          wixtarConfig.value = cfgRes?.data ?? null;
        } catch (err) {
          console.error('載入 Wixtar 配置失敗:', err);
          wixtarConfig.value = null;
        }
        await loadWixtarStampIssueSetting();
      } catch (err) {
        console.error('載入 Wixtar 會員系統類型失敗:', err);
        isWixtarClient.value = false;
      }
    };

    onMounted(() => {
      fetchCampaigns();
      loadWixtarContext();
    });

    return {
      pageTitle,
      campaigns,
      loading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      pagination,
      fetchCampaigns,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleDelete,
      getRuleTypeText,
      getStatusText,
      getStatusClass,
      formatDate,
      isWixtarClient,
      canEnableWixtarStampIssue,
      wixtarTransactionStampIssueEnabled,
      wixtarStampSettingSaving,
      saveWixtarStampIssueSetting
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.wixtar-stamp-notice {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #fff8e6;
  border: 1px solid #f0d78c;
  border-radius: 8px;
  color: #7a5b00;
  font-size: 13px;
  line-height: 1.5;
}

.wixtar-stamp-notice > i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #c98a00;
  font-size: 18px;
}

.wixtar-stamp-notice-title {
  margin: 0 0 4px;
  font-weight: 600;
  color: #6b4f00;
}

.wixtar-stamp-notice p {
  margin: 0 0 8px;
}

.wixtar-stamp-notice p:last-child {
  margin-bottom: 0;
}

.wixtar-stamp-notice a {
  color: #5746e3;
  font-weight: 600;
}

.wixtar-stamp-switch-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 10px 0;
}

.wixtar-stamp-notice .notice-on {
  color: #1b5e20;
}

.wixtar-stamp-notice .notice-off {
  color: #8a5a00;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #2e7d32;
}

.toggle-status.off {
  color: #757575;
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}
</style>
